// Helper functions for preloading assets
// Create a persistent cache to store preloaded images across navigation
// Use sessionStorage to persist the list of loaded images
const getImageCache = () => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const cachedImages = sessionStorage.getItem('ridleyImageCache');
    return cachedImages ? JSON.parse(cachedImages) : {};
  }
  return {};
};

const imageCache = getImageCache();

// Save the cache state to sessionStorage
const updateImageCache = (src) => {
  imageCache[src] = true;
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.setItem('ridleyImageCache', JSON.stringify(imageCache));
  }
};

export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    // Check if src is valid
    if (!src) {
      console.warn(`Invalid image source: ${src}`);
      resolve(null);
      return;
    }
    
    // Check our own cache first
    if (imageCache[src]) {
      //console.log(`Image found in persistent cache: ${src}`);
      resolve(src);
      return;
    }
    
    // If the image is already in browser cache, resolve immediately
    const cachedImg = new Image();
    cachedImg.src = src;
    if (cachedImg.complete) {
      //console.log(`Image already cached in browser: ${src}`);
      updateImageCache(src); // Store in our persistent cache
      resolve(src);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      //console.log(`Image loaded: ${src}`);
      updateImageCache(src); // Store in our persistent cache
      resolve(src);
    };
    img.onerror = () => {
      console.warn(`Failed to load image: ${src}, but continuing anyway`);
      resolve(null); // Resolve with null to indicate error but not block
    };
    img.src = src;
    
    // Add a timeout for each individual image, but make it shorter
    setTimeout(() => {
      if (!img.complete) {
        console.warn(`Image load timed out: ${src}`);
        // Continue with the image anyway - it might load later
        resolve(src);
      }
    }, 1000);
  });
};

// Preload critical images for the site
// Helper that would typically add a cache buster to image URLs
// But we deliberately don't use cache busters for our home page images
// since we want to preserve the original URLs for CSS styling and hover effects
export const getCacheBusterUrl = (url) => {
  // Return the original URL without any modifications
  // This ensures CSS styling with background-images works correctly
  // and hover effects remain applied to the same elements
  return url;
};

export const preloadCriticalAssets = () => {
  return new Promise((resolve) => {
    try {
      // Create an array to store the image promises
      const imagePromises = [];
      
      // Check if we're returning to the home page
      const hasVisitedHome = sessionStorage.getItem('hasVisitedHome') === 'true';
      if (hasVisitedHome) {
        console.log('Return visit to home page - will use cached images if available');
      }
      
      // Mark that we've visited the home page (don't clear the cache)
      sessionStorage.setItem('hasVisitedHome', 'true');
      
      // Import logo image
      const logoPromise = import('../images/logo-white.png')
        .then(logoModule => {
          //console.log("Logo loaded:", logoModule.default);
          return preloadImage(logoModule.default);
        })
        .catch(error => {
          console.warn("Failed to import logo:", error);
          return null;
        });
      
      imagePromises.push(logoPromise);
      
      // Import Ridley image
      const ridleyPromise = import('../images/ridley.png')
        .then(ridleyModule => {
          //console.log("Ridley image loaded:", ridleyModule.default);
          return preloadImage(ridleyModule.default);
        })
        .catch(error => {
          console.warn("Failed to import Ridley image:", error);
          return null;
        });
      
      imagePromises.push(ridleyPromise);
      
      // Import bike dealer image
      const dealerPromise = import('../images/ridley-dealers.png')
        .then(module => {
          //console.log("Dealer image loaded:", module.default);
          return preloadImage(module.default);
        })
        .catch(error => {
          console.warn("Failed to import dealer image:", error);
          return null;
        });
        
      imagePromises.push(dealerPromise);
      
      // Add any hardcoded bike images that are used as fallbacks to ensure they're loaded
      const fallbackImages = [
        import('../images/bike.png').then(m => preloadImage(m.default)).catch(() => null),
        import('../images/icon.png').then(m => preloadImage(m.default)).catch(() => null)
      ];
      
      imagePromises.push(...fallbackImages);
      
      // Preload bike category (pick4) images if available
      if (window.preloadedHomeData && window.preloadedHomeData.pick4Data) {
        const bikeImages = window.preloadedHomeData.pick4Data
          .filter(item => item && item.data && item.data.obrazek)
          .map(item => {
            //console.log("Preloading bike category image:", item.data.obrazek);
            return preloadImage(item.data.obrazek);
          });
          
        imagePromises.push(...bikeImages);
      }
      
      // Preload news (novinky) images if available
      if (window.preloadedHomeData && window.preloadedHomeData.novinkyData) {
        const novinkyImages = window.preloadedHomeData.novinkyData
          .filter(item => item && item.data && item.data.image)
          .map(item => {
            //console.log("Preloading news image:", item.data.image);
            return preloadImage(item.data.image);
          });
          
        imagePromises.push(...novinkyImages);
      }
      
      // Wait for all images to load or timeout
      Promise.all(imagePromises)
        .then((results) => {
          //console.log("All critical assets loaded successfully", results.filter(Boolean).length, "of", imagePromises.length);
          resolve(true);
        })
        .catch((error) => {
          console.log("Failed to load some critical assets, continuing anyway", error);
          resolve(true);
        });
      
      // Backup timeout to ensure we don't block loading for too long
      setTimeout(() => {
        //console.log("Preloading assets timeout triggered");
        resolve(true);
      }, 800); // Reduced from 2 seconds to speed up the loading animation
    } catch (error) {
      console.error("Exception in preloadCriticalAssets:", error);
      resolve(false);
    }
  });
};